import { navigate } from "./navigation";
import type { ReviewRole } from "../../shared/review-access";

export type ReviewPosition =
  | { screen: "intro"; page: number }
  | { screen: "layer"; layerId: string; focusId?: string }
  | { screen: "complete"; layerId?: string; focusId?: string };

interface ReviewRouteDefaults {
  defaultLayerId: string;
  introPages: number;
  layerIds: string[];
}

const ownedParameters = ["view", "intro", "layer", "focus"] as const;

export function reviewLandingPath(role: ReviewRole | undefined): "/" | "/queue" {
  return role === "author" ? "/" : "/queue";
}

export function hasExplicitReviewPosition(search = window.location.search): boolean {
  // A link carrying a layer or focus is asking for that position just as
  // clearly as one carrying a view; the stored session must not override it.
  const parameters = new URLSearchParams(search);
  return parameters.has("view") || parameters.has("layer") || parameters.has("focus");
}

export function readReviewPosition(
  search: string,
  { defaultLayerId, introPages, layerIds }: ReviewRouteDefaults,
): ReviewPosition {
  const parameters = new URLSearchParams(search);
  const view = parameters.get("view");
  const requestedLayer = parameters.get("layer");
  const layerId = requestedLayer && layerIds.includes(requestedLayer) ? requestedLayer : defaultLayerId;
  const focusId = parameters.get("focus") || undefined;

  if (view === "receipt") return { screen: "complete", layerId, focusId };
  if (view === "review" || introPages === 0) return { screen: "layer", layerId, focusId };

  const requestedPage = Number(parameters.get("intro") ?? 0);
  const page = Number.isFinite(requestedPage)
    ? Math.max(0, Math.min(Math.max(0, introPages - 1), Math.floor(requestedPage)))
    : 0;
  return { screen: "intro", page };
}

export function reviewPositionPath(position: ReviewPosition, href = window.location.href): string {
  const url = new URL(href);
  for (const parameter of ownedParameters) url.searchParams.delete(parameter);

  if (position.screen === "intro") {
    url.searchParams.set("view", "intro");
    url.searchParams.set("intro", String(position.page));
  } else if (position.screen === "layer") {
    url.searchParams.set("view", "review");
    url.searchParams.set("layer", position.layerId);
    if (position.focusId) url.searchParams.set("focus", position.focusId);
  } else {
    url.searchParams.set("view", "receipt");
    if (position.layerId) url.searchParams.set("layer", position.layerId);
    if (position.focusId) url.searchParams.set("focus", position.focusId);
  }

  return `${url.pathname}${url.search}${url.hash}`;
}

export function writeReviewPosition(
  position: ReviewPosition,
  options: { notify?: boolean; replace?: boolean } = {},
) {
  navigate(reviewPositionPath(position), options);
}
